import { createAction } from '@gef-ui/utils/fsaActions';
import omit from "ramda/es/omit";
import pick from "ramda/es/pick";
import prop from "ramda/es/prop";
import alwaysNull from "ramda-extension/es/alwaysNull";
import keyMirror from "ramda-extension/es/keyMirror";
import { Subject, identity } from 'rxjs';
import { createFieldRecord } from '../../../utils/data';
import { getValidationTypes } from '../../../validations/globalConfig';
export const actionNames = keyMirror({
    REGISTER: null,
    UNREGISTER: null,
    SET_VALUE: null,
    BLUR: null,
    INIT_FORM_DATA: null,
    INIT_FORM_FIELD_STATE: null,
    INIT_GLOBAL_ERRORS: null,
    INIT_FORM_STATE: null,
    VALIDATE_FIELD: null,
    VALIDATE_FIELDS: null,
    VALIDATE_ARRAY_ROW: null,
    VALIDATE_FORM: null,
    VALIDATE_FIELD_ARRAY: null,
    INSERT_GLOBAL_ERRORS: null,
    APPEND_TO_LIST: null,
    PREPEND_TO_LIST: null,
    REMOVE_FROM_LIST: null,
    CLEAR_LIST: null,
    DEEP_MERGE_DATA: null,
    REPLACE_DATA: null,
    RESET_FORM: null,
    SET_FIELD_DISABLED: null,
    DEEP_MERGE_ROW: null,
    REPLACE_ROW: null,
    CLEAR_ROW: null,
    MANUAL_FOCUS: null,
    DISABLE_FORM: null,
    SET_VALUE_BY_DEEP_PATH: null,
    CLEAR_FIELDS_ERRORS: null
});
const createDefaultMeta = ({ fieldName, fieldIndices, validationType, section, validationGroups, expandDependent, validate })=>{
    const fieldRecord = createFieldRecord(fieldName, fieldIndices);
    return {
        affectedFields: [
            fieldRecord
        ],
        fieldRecord,
        fieldName,
        fieldIndices,
        validationType,
        section,
        validationGroups,
        expandDependent,
        validate
    };
};
export const createValidationGroups = ({ groups, section })=>{
    if (!groups) {
        return {
            validationGroups: {
                dirtyGroup: getValidationTypes(),
                pristineGroup: getValidationTypes(),
                notPresentGroup: getValidationTypes()
            },
            section
        };
    } else {
        return {
            validationGroups: groups,
            section
        };
    }
};
const defaultOmit = omit([
    'validationType',
    'fieldIndices',
    'section',
    'validationGroups',
    'expandDependent',
    'groups',
    'validate'
]);
/**
 * register field action
 *
 * @documented
 * @category Control
 * @import import { registerField } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {boolean} [input.initiallyDirty]
 * @param {boolean} [input.defaultValue]
 * @param {String} [input.section]
 * @example
 *
 * 	registerField({ fieldName, fieldIndices, initiallyDirty, section })
 *
 */ export const registerField = createAction(actionNames.REGISTER, defaultOmit, createDefaultMeta);
/**
 * unregister field action
 *
 * @documented
 * @category Control
 * @import import { unregisterField } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {boolean} [input.initiallyDirty]
 * @param {String} [input.section]
 * @example
 *
 * 	unregisterField({ fieldName, fieldIndices, initiallyDirty, section })
 *
 */ export const unregisterField = createAction(actionNames.UNREGISTER, defaultOmit, createDefaultMeta);
/**
 * setValue to formData action
 *
 * @documented
 * @category Control
 * @import import { setValue } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {*} input.value
 * @param {Boolean} input.keepPristine - if truthy then it won't dirty field or form state when pristine
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {String} [input.validationType]
 * @example
 *
 * 	setValue({ fieldName, value: resolvedValue, validationType, fieldIndices })
 *
 */ export const setValue = createAction(actionNames.SET_VALUE, defaultOmit, createDefaultMeta);
/**
 * prepend data to list
 *
 * @documented
 * @category Control
 * @import import { prependToList } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.deepPath - deep path of array
 * @param {*} input.value
 * @example
 *
 * 	prependToList({ deepPath: ['minions'], value: {} })
 *
 */ export const prependToList = createAction(actionNames.PREPEND_TO_LIST, identity);
/**
 * append data to list
 *
 * @documented
 * @category Control
 * @import import { appendToList } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.deepPath - deep path of array
 * @param {*} input.value
 * @example
 *
 * 	appendToList({ deepPath: ['minions'], value: {} })
 *
 */ export const appendToList = createAction(actionNames.APPEND_TO_LIST, identity);
/**
 * remove index from list
 *
 * @documented
 * @category Control
 * @import import { removeFromList } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.deepPath - deep path of array
 * @param {number} input.index
 * @example
 *
 * 	removeFromList({ deepPath: ['minions'], index: 0 })
 *
 */ export const removeFromList = createAction(actionNames.REMOVE_FROM_LIST, identity);
/**
 * replace data in row
 *
 * @documented
 * @category Control
 * @import import { replaceRow } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.arrayName - array name from descriptor
 * @param {number} input.arrayIndices - indices for array, can be nested and sorted by path of nesting
 * @param {number} input.data - data to replace
 * @example
 *
 * 	replaceRow({ arrayName: 'minions', arrayIndices: [0], data: {foo: 'bar'} })
 *
 */ export const replaceRow = createAction(actionNames.REPLACE_ROW, identity);
/**
 * deep merge data in row
 *
 * @documented
 * @category Control
 * @import import { deepMergeRow } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.arrayName - array name from descriptor
 * @param {number} input.arrayIndices - indices for array, can be nested and sorted by path of nesting
 * @param {number} input.data - data to merge
 * @example
 *
 * 	deepMergeRow({ arrayName: 'minions', arrayIndices: [0], data: {foo: 'bar'} })
 *
 */ export const deepMergeRow = createAction(actionNames.DEEP_MERGE_ROW, identity);
/**
 * clear data in row
 *
 * @documented
 * @category Control
 * @import import { clearRow } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.arrayName - array name from descriptor
 * @param {number} input.arrayIndices - indices for array, can be nested and sorted by path of nesting
 * @example
 *
 * 	clearRow({ arrayName: 'minions', arrayIndices: [0] })
 *
 */ export const clearRow = createAction(actionNames.CLEAR_ROW, identity);
/**
 * clear list and default value to empty array
 *
 * @documented
 * @category Control
 * @import import { clearList } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Array} deep path of array
 * @example
 *
 * 	clearList(['minions'])
 *
 */ export const clearList = createAction(actionNames.CLEAR_LIST, identity);
const createDataModificationAction = (actionType)=>createAction(actionType, prop('data'), (input)=>({
            ...createValidationGroups(input),
            validate: input.validate
        }));
/**
 * deep merge formData
 *
 * @documented
 * @category Control
 * @import import { deepMergeData } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Object} input.data - data to merge
 * @param {boolean} [input.validate] - validate affected fields
 * @param {Object} [input.groups = all validations on all types]
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	deepMergeData({data: { firsName: 'xxx', lastName: 'xxx' }})
 *
 */ export const deepMergeData = createDataModificationAction(actionNames.DEEP_MERGE_DATA);
/**
 * replace formData
 *
 * @documented
 * @category Control
 * @import import { replaceData } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Object} input.data - data to use
 * @param {boolean} [input.validate] - validate affected fields
 * @param {Object} [input.groups = all validations on all types]
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	replaceData({data: { firsName: 'xxx', lastName: 'xxx' }})
 *
 */ export const replaceData = createDataModificationAction(actionNames.REPLACE_DATA);
/**
 * validate fields with given groups
 *
 * @documented
 * @category Control
 * @import import { validateFields } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array[]} input.fieldsToValidate[]
 * @param {String} input.fieldsToValidate[][0].fieldName
 * @param {Number[]} [input.fieldsToValidate[][1].fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {Object} [input.groups = all validations on all types]
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	validateFields({fieldsToValidate: [['minionFirstName', 0],['minionPrice']]})
 *
 * {
 *  dirtyGroup: getValidationTypes(),
 *	pristineGroup: getValidationTypes(),
 *	notPresentGroup: getValidationTypes(),
 *	}
 *
 */ export const validateFields = createAction(actionNames.VALIDATE_FIELDS, prop('fieldsToValidate'), createValidationGroups);
/**
 * validate array row with given groups
 *
 * @documented
 * @category Control
 * @import import { validateArrayRow } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.arrayName
 * @param {Number} input.rowNo
 * @param {Object} [input.groups = all validations on all types]
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	validateArrayRow({arrayName: 'minions', rowNo: 0})
 *
 * {
 *  dirtyGroup: getValidationTypes(),
 *	pristineGroup: getValidationTypes(),
 *	notPresentGroup: getValidationTypes(),
 *	}
 *
 */ export const validateArrayRow = createAction(actionNames.VALIDATE_ARRAY_ROW, pick([
    'arrayName',
    'rowNo'
]), createValidationGroups);
/**
 * validate form
 *
 * @documented
 * @category Control
 * @import import { validateForm } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Object} [input.groups = all validations on all types]
 * @param {Array[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {Array[]} [input.groups.pristineGroup] - validation types for given group
 * @param {Array[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	validateForm({groups: {
 *  dirtyGroup: getValidationTypes(),
 *	pristineGroup: getValidationTypes(),
 *	notPresentGroup: getValidationTypes(),
 *		})
 *
 */ export const validateForm = createAction(actionNames.VALIDATE_FORM, alwaysNull, createValidationGroups);
/**
 * validate fieldArray
 *
 * @documented
 * @category Control
 * @import import { validateFieldArray } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.arrayName - name of array
 * @param {Object} [input.groups = all validations on all types]
 * @param {Array[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {Array[]} [input.groups.pristineGroup] - validation types for given group
 * @param {Array[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	validateFieldArray({arrayName: 'minions'})
 *
 */ export const validateFieldArray = createAction(actionNames.VALIDATE_FIELD_ARRAY, defaultOmit, createValidationGroups);
/**
 * runs validations for given field
 *
 * @documented
 * @category Control
 * @import import { validateField } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {boolean} [input.expandDependent=true] - validate dependent fields
 * @param {Object} [input.groups = all validations on all types]
 * @param {Array[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {Array[]} [input.groups.pristineGroup] - validation types for given group
 * @param {Array[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	validateField({ fieldName, fieldIndices })
 *
 */ export const validateField = createAction(actionNames.VALIDATE_FIELD, defaultOmit, createDefaultMeta);
/**
 * Insert global errors. Takes array of global errors. fieldRecord, section and table paths are automatically provided,
 * if not present. Then grouped by fieldRecord to create global errors state.
 *
 * @documented
 * @category Control
 * @import import { insertGlobalErrors } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @example
 *
 * 	insertGlobalErrors([
 * 	{
 * 		errorMessage: 'err msg',
 * 		fieldIndices: undefined,
 * 		fieldName: 'email',
 * 		type: 'ERROR',
 * 	},
 * ]);
 *
 */ export const insertGlobalErrors = createAction(actionNames.INSERT_GLOBAL_ERRORS);
/**
 * blur
 *
 * @documented
 * @category Control
 * @import import { blur } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {Object} [input.groups = all validations on all types]
 * @param {Array[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {Array[]} [input.groups.pristineGroup] - validation types for given group
 * @param {Array[]} [input.groups.notPresentGroup] - validation types for given group
 * @example
 *
 * 	blur({ fieldName, fieldIndices })
 *
 */ export const blur = createAction(actionNames.BLUR, defaultOmit, createDefaultMeta);
/**
 * reset form to its initial data
 *
 * @documented
 * @category Control
 * @import import { resetForm } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} [input]
 * @param {Object} [input.initialFormData] - supply new initial form data to form.
 * @param {Object} [input.keepData=false] - Keep actual data as new initData. Overrides initialFormData param.
 * @param {Object} [input.pristine=true] - mark all fields and form as pristine. Keeps initiallyDirty in mind.
 * @param {Object} [input.validate=true] - validate form
 * @param {Object} [input.groups]
 * @param {Array[]} [input.groups.dirtyGroup=ALL] - validation types for given group
 * @param {Array[]} [input.groups.pristineGroup=NONE] - validation types for given group
 * @param {Array[]} [input.groups.notPresentGroup=NONE] - validation types for given group

 * @example
 *
 * 	resetForm()
 *
 */ export const resetForm = createAction(actionNames.RESET_FORM, prop('initialFormData'), ({ pristine = true, validate = true, keepData = false, groups = {} } = {})=>({
        pristine,
        validate,
        keepData,
        validationGroups: {
            dirtyGroup: getValidationTypes(),
            ...groups
        }
    }));
/**
 * manually focus field
 *
 * @documented
 * @category Control
 * @import import { manualFocus } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {String} [input.id] - by default it creates id from fieldName and formName
 * @example
 *
 * 	manualFocus({ fieldName })
 *
 */ export const manualFocus = createAction(actionNames.MANUAL_FOCUS, defaultOmit, createDefaultMeta);
/**
 * disable/enable form
 *
 * @documented
 * @category Control
 * @import import { disableForm } from '@gef-ui/form-core/stream/transfer/control'
 * @param {boolean} disabled
 * @example
 *
 * 	disableForm(true)
 *
 */ export const disableForm = createAction(actionNames.DISABLE_FORM, identity);
/**
 * setValueByDeepPath to formData action
 *
 * @documented
 * @category Control
 * @import import { setValueByDeepPath } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array} input.deepPath - path in formData
 * @param {*} input.value
 *
 * @example
 *
 * 	setValueByDeepPath({ deepPath, value })
 *
 */ export const setValueByDeepPath = createAction(actionNames.SET_VALUE_BY_DEEP_PATH, defaultOmit);
/**
 * clear fields errors without running validations
 * use it with caution
 *
 * @documented
 * @category Control
 * @import import { clearFieldsErrors } from '@gef-ui/form-core/stream/transfer/control'
 * @param {Object} input
 * @param {Array[]} input.fields[] - clear everything if empty
 * @param {String} input.fields[][0].fieldName
 * @param {Number[]} [input.fields[][1].fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {Object} [input.pristine = turn field pristine]
 * @example
 *
 * 	clearFieldsErrors({fields: [['minionFirstName', 0],['minionPrice']]})
 *
 */ export const clearFieldsErrors = createAction(actionNames.CLEAR_FIELDS_ERRORS, prop('fields'), identity);
// private actions, no need to document them
export const initFormData = createAction(actionNames.INIT_FORM_DATA);
export const initFormFieldState = createAction(actionNames.INIT_FORM_FIELD_STATE);
export const initGlobalErrors = createAction(actionNames.INIT_GLOBAL_ERRORS);
export const initFormState = createAction(actionNames.INIT_FORM_STATE);
export const setFieldDisabled = createAction(actionNames.SET_FIELD_DISABLED, pick([
    'disabled'
]), createDefaultMeta);
export default (()=>{
    const control$ = new Subject();
    return control$;
});
